import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, FormControlLabel, IconButton, Paper, Radio, RadioGroup, Typography } from "@mui/material";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { localizeValidationSummary } from "../../../utils/dataLoadValidationSummary";
import { useResolveShiftConflicts } from "../hooks";

// --- Shared Types ---

export type ConflictDetails = {
	nurse_id?: string;
	start_date?: string;
	start_time?: string;
	end_time?: string;
	existing_start_date?: string;
	existing_start_time?: string;
	existing_end_time?: string;
};

export type ErrorDetail = {
	error: string;
	error_type?: string;
	message?: ConflictDetails | string;
	nurse_name?: string;
};

export type FailedShift = [number, ErrorDetail];

export type AmbiguousNurse = {
	name: string;
	suspects: string[];
};

export type SuccessfulMatch = {
	employee_name: string;
	nurse_name: string;
};

export type ConflictWindow = { start: string; end: string };

export type ConflictOption = {
	source: "file" | "existing";
	kind: "shift" | "pto";
	window: ConflictWindow;
	location?: string | null;
	reason?: string | null;
};

export type ShiftPtoConflict = {
	conflict_id: string;
	nurse_id: number;
	nurse_name: string;
	employee_name: string;
	options: ConflictOption[];
};

export type ConflictDecision = { conflict_id: string; nurse_id: number; keep: ConflictOption };

export type ResolveConflictsResult = {
	applied: number;
	skipped: number;
	errors: Array<{ conflict_id: string; nurse_id: number; error: string }>;
	reactivated_nurses: SuccessfulMatch[];
};

/**
 * What occupied the window a skipped PTO row wanted. The DB constraint is type-agnostic, so the
 * blocker is not always PTO: "existing" is a stored preference of any type, "file" is an earlier
 * row of the same upload, "concurrent" is a write that landed between the check and the insert.
 * The window/type are re-read for "concurrent" too, so they are only null when the blocker is gone.
 */
export type SkippedPtoConflict = {
	origin: "existing" | "file" | "concurrent";
	type: string | null;
	reason: string | null;
	start_date: string | null;
	end_date: string | null;
};

/** PTO row the file carried that could not be loaded because something already occupies its window. */
export type SkippedPto = {
	nurse_id: number;
	nurse_name: string;
	employee_name: string;
	start_date: string;
	end_date: string;
	reason: string;
	blocked_by: SkippedPtoConflict;
};

const PTO_PREFERENCE_TYPE = "avoid";
const UI_VISIBLE_ERROR_TYPE = "UIVisibleError";

export type LoadShiftsResult = {
	parsing_error: Array<{ name: string; reason: string; row: number }>;
	succed_nurses: SuccessfulMatch[];
	failed_shifts: Array<FailedShift | [number, string]>;
	overlap_shifts: Array<{
		nurse_id: number;
		nurse_name: string;
		employee_name: string;
		shift1: { start_time: string; end_time: string };
		shift2: { start_time: string; end_time: string };
	}>;
	not_found_nurses: unknown[];
	ambigious_nurses: AmbiguousNurse[];
	no_time_nurses: unknown[];
	reactivated_nurses: SuccessfulMatch[];
	conflicts: ShiftPtoConflict[];

	skipped_ptos: SkippedPto[];
	validation_summary?: string | null;
};

export type LoadRecurringResult = {
	parsing_error: Array<{ name: string; reason: string; row: number }>;
	succed_nurses: SuccessfulMatch[];
	failed_shifts: Array<FailedShift | [number, string]>;
	not_found_nurses: unknown[];
	ambigious_nurses: AmbiguousNurse[];
	reactivated_nurses: SuccessfulMatch[];
	validation_summary?: string | null;
};

// --- Helpers ---

export const isShiftsResult = (r: LoadShiftsResult | LoadRecurringResult | null): r is LoadShiftsResult => !!r && "no_time_nurses" in r;

const formatItem = (item: unknown): string => {
	if (item === null || item === undefined || item === "") return "";
	if (typeof item === "string" || typeof item === "number") return String(item);
	if (Array.isArray(item)) {
		const filtered = item.map((v) => formatItem(v)).filter(Boolean);
		return filtered.join(" – ");
	}
	if (item && typeof item === "object" && "name" in item) return String((item as { name: unknown }).name);
	return JSON.stringify(item);
};

const safeString = (val: unknown): string => {
	if (val === null || val === undefined) return "";
	if (typeof val === "string" || typeof val === "number") return String(val);
	if (val && typeof val === "object" && "toISOString" in val) {
		try {
			return (val as { toISOString: () => string }).toISOString();
		} catch {
			return String(val);
		}
	}
	return String(val);
};

const formatDate = (val: unknown): string => {
	const str = safeString(val);
	return str.includes("T") ? str.split("T")[0] : str;
};

const CONFLICT_SOURCE_KEY: Record<ConflictOption["source"], string> = {
	file: "onboarding_load.conflicts.source_file",
	existing: "onboarding_load.conflicts.source_existing",
};
const CONFLICT_KIND_KEY: Record<ConflictOption["kind"], string> = {
	shift: "onboarding_load.conflicts.kind_shift",
	pto: "onboarding_load.conflicts.kind_pto",
};

const formatConflictWindow = (window: ConflictWindow): string => {
	const start = DateTime.fromISO(window.start);
	const end = DateTime.fromISO(window.end);
	if (!start.isValid || !end.isValid) return `${window.start} - ${window.end}`;
	return `${start.toLocaleString(DateTime.DATE_SHORT)} ${start.toLocaleString(DateTime.TIME_SIMPLE)} - ${end.toLocaleString(DateTime.TIME_SIMPLE)}`;
};

const conflictOptionLabel = (option: ConflictOption, t: TFunction): string => {
	const origin = t(CONFLICT_SOURCE_KEY[option.source]);
	const kind = t(CONFLICT_KIND_KEY[option.kind]);
	const suffix = option.kind === "shift" && option.location ? t("onboarding_load.conflicts.location_suffix", { location: option.location }) : "";
	return t("onboarding_load.conflicts.option_label", { origin, kind, window: formatConflictWindow(option.window), suffix });
};

/** Phrase the blocker so the operator can tell "nothing to do" apart from "this PTO landed nowhere". */
const describePtoConflict = (conflict: SkippedPtoConflict, t: TFunction): string => {
	const window =
		conflict.start_date && conflict.end_date
			? t("onboarding_load.skipped_pto.window_suffix", { window: `${formatDate(conflict.start_date)} – ${formatDate(conflict.end_date)}` })
			: "";
	if (conflict.origin === "concurrent") return t("onboarding_load.skipped_pto.blocked_concurrent", { window });
	if (conflict.origin === "file") return t("onboarding_load.skipped_pto.blocked_by_file", { window });
	if (conflict.type && conflict.type !== PTO_PREFERENCE_TYPE) return t("onboarding_load.skipped_pto.blocked_by_other_type", { type: conflict.type, window });
	return t("onboarding_load.skipped_pto.blocked_by_existing", { window });
};

// --- Helper Components ---

export const ErrorSection = ({ title, items }: { title: string; items: unknown[] }) => {
	if (items.length === 0) return null;
	return (
		<Box>
			<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
				{title} ({items.length})
			</Typography>
			{items.map((item, idx) => {
				const formatted = formatItem(item);
				return formatted ? (
					<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
						{formatted}
					</Typography>
				) : null;
			})}
		</Box>
	);
};

export const AmbiguousSection = ({ items }: { items: AmbiguousNurse[] }) => {
	if (items.length === 0) return null;
	return (
		<Box>
			<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
				Ambiguous Nurses ({items.length})
			</Typography>
			{items.map((item, idx) => (
				<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
					{item.name}
					{item.suspects.length > 0 && ` → suspects: ${item.suspects.join(", ")}`}
				</Typography>
			))}
		</Box>
	);
};

export const FailedShiftCard = ({ nurse_id, error_detail }: { nurse_id: number; error_detail: ErrorDetail | string }) => {
	const { t } = useTranslation();
	const nurseName = typeof error_detail !== "string" ? error_detail.nurse_name : undefined;
	const nurseLabel = nurseName ? ` (${nurseName})` : ` (ID: ${nurse_id})`;

	if (typeof error_detail === "string") {
		return (
			<Box sx={{ mb: 2, p: 2, border: "1px solid #ef5350", borderRadius: 1 }}>
				<Typography variant="body2" sx={{ mb: 0.5 }}>
					<strong>Nurse ID:</strong> {nurse_id}
				</Typography>
				<Typography variant="body2" sx={{ color: "#d32f2f" }}>
					<strong>Error:</strong> {error_detail}
				</Typography>
				<Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic", color: "#e65100" }}>
					Note: All shifts for this nurse{nurseLabel} were not uploaded due to this error.
				</Typography>
			</Box>
		);
	}

	const conflictDetails = typeof error_detail.message === "object" && error_detail.message ? error_detail.message : null;
	const errorMessage =
		error_detail.error_type === "conflict shift"
			? "This shift conflicts with an existing shift at the same time for this nurse."
			: error_detail.error_type === UI_VISIBLE_ERROR_TYPE && typeof error_detail.message === "string"
				? t(error_detail.message)
				: error_detail.error_type
					? `Error type: ${error_detail.error_type}`
					: error_detail.error;

	return (
		<Box sx={{ mb: 2, p: 2, border: "1px solid #ef5350", borderRadius: 1 }}>
			{conflictDetails && (
				<>
					<Box sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 700 }}>
							Shift we tried to insert:
						</Typography>
						<Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
							Nurse ID: {safeString(conflictDetails.nurse_id) || nurse_id}
						</Typography>
						<Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
							Date: {formatDate(conflictDetails.start_date)}
						</Typography>
						<Typography variant="body2" sx={{ ml: 2 }}>
							Time: {safeString(conflictDetails.start_time)} - {safeString(conflictDetails.end_time)}
						</Typography>
					</Box>
					{conflictDetails.existing_start_date && (
						<Box sx={{ mb: 2 }}>
							<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 700 }}>
								Existing conflicting shift:
							</Typography>
							<Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
								Nurse ID: {safeString(conflictDetails.nurse_id)}
							</Typography>
							<Typography variant="body2" sx={{ ml: 2, mb: 0.5 }}>
								Date: {formatDate(conflictDetails.existing_start_date)}
							</Typography>
							<Typography variant="body2" sx={{ ml: 2 }}>
								Time: {safeString(conflictDetails.existing_start_time)} - {safeString(conflictDetails.existing_end_time)}
							</Typography>
						</Box>
					)}
				</>
			)}
			<Box>
				<Typography variant="body2" sx={{ mb: 0.5, fontWeight: 700, color: "#d32f2f" }}>
					Reason for failure:
				</Typography>
				<Typography variant="body2" sx={{ ml: 2, color: "#d32f2f" }}>
					{errorMessage}
				</Typography>
			</Box>
			<Typography variant="body2" sx={{ mt: 1.5, fontStyle: "italic", color: "#e65100" }}>
				Note: All shifts for this nurse{nurseLabel} were not uploaded due to this error.
			</Typography>
		</Box>
	);
};

// --- Copyable Summary ---

function SummaryCopyButton({ text, dataTestId }: { text: string; dataTestId: string }) {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(async () => {
		if (!(await copyToClipboard(text))) return;
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [copyToClipboard, text]);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			{copied && (
				<Typography variant="caption" sx={{ color: "success.main" }}>
					{t("ops.backoffice.data.load.summary.copied")}
				</Typography>
			)}
			<IconButton
				size="small"
				onClick={handleCopy}
				title={t("ops.backoffice.data.load.summary.copy")}
				aria-label={t("ops.backoffice.data.load.summary.copy")}
				data-testid={dataTestId}
			>
				<ContentCopyIcon fontSize="small" />
			</IconButton>
		</Box>
	);
}

function SummaryText({ text }: { text: string }) {
	return (
		<Typography variant="body2" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: "0.8rem" }}>
			{text}
		</Typography>
	);
}

export function CopyableSummaryBox({ text, title }: { text: string; title: string }) {
	return (
		<Paper sx={{ p: 2, mb: 3, bgcolor: "#f5f5f5", border: "1px solid #e0e0e0" }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
				<SummaryCopyButton text={text} dataTestId="onboarding-summary-copy-button" />
			</Box>
			<SummaryText text={text} />
		</Paper>
	);
}

/** The server-built aggregate report, collapsed by default: the per-row detail above is what the
 * uploader acts on first, while this is the same artifact Ops reads later on the drill-down. */
export function CollapsibleSummaryBox({ text, title, timezone }: { text: string; title: string; timezone?: string }) {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const localizedText = localizeValidationSummary(text, t, timezone);

	return (
		<Accordion expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)} sx={{ mb: 3, "&:before": { display: "none" } }}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />}>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{title}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
					<SummaryCopyButton text={localizedText} dataTestId="onboarding-validation-summary-copy-button" />
				</Box>
				<SummaryText text={localizedText} />
			</AccordionDetails>
		</Accordion>
	);
}

function buildShiftsSummaryText(result: LoadShiftsResult | LoadRecurringResult, t: TFunction): string {
	const lines: string[] = [];

	// Employees reactivated because they were inactive but have shifts in the file
	if (result.reactivated_nurses.length > 0) {
		lines.push("Reactivated employees:");
		for (const match of result.reactivated_nurses) {
			lines.push(`  - ${match.employee_name} → ${match.nurse_name}`);
		}
		lines.push("");
	}

	// Not found + ambiguous nurses
	const notFoundItems = result.not_found_nurses.map((item) => formatItem(item)).filter(Boolean);
	const ambiguousItems = result.ambigious_nurses;

	if (notFoundItems.length > 0 || ambiguousItems.length > 0) {
		lines.push("People not found:");
		for (const name of notFoundItems) {
			lines.push(`  - ${name}`);
		}
		for (const item of ambiguousItems) {
			const suspectsPreview = item.suspects.slice(0, 3).join(", ");
			const more = item.suspects.length > 3 ? ` (+${item.suspects.length - 3} more)` : "";
			lines.push(`  - ${item.name} (ambiguous, possible matches: ${suspectsPreview}${more})`);
		}
		lines.push("");
	}

	// Failed shifts
	const shiftsResult = isShiftsResult(result) ? result : null;
	const failedShifts = shiftsResult?.failed_shifts ?? (result.failed_shifts as Array<FailedShift | [number, string]>);

	if (failedShifts.length > 0) {
		lines.push("Failed shifts:");
		for (const f of failedShifts) {
			if (!Array.isArray(f) || f.length !== 2) continue;
			const [nurseId, detail] = f;

			if (typeof detail === "string") {
				lines.push(`  - Nurse ID ${nurseId}: ${detail}`);
				continue;
			}

			const nurseName = detail.nurse_name ? ` (${detail.nurse_name})` : "";
			if (detail.error_type === "conflict shift" && typeof detail.message === "object" && detail.message) {
				const c = detail.message;
				const tried = `${formatDate(c.start_date)} ${safeString(c.start_time)}-${safeString(c.end_time)}`;
				const existing = c.existing_start_date
					? `${formatDate(c.existing_start_date)} ${safeString(c.existing_start_time)}-${safeString(c.existing_end_time)}`
					: "unknown";
				lines.push(`  - Nurse ${nurseId}${nurseName}: Conflict - tried ${tried}, existing ${existing}`);
			} else if (detail.error_type === UI_VISIBLE_ERROR_TYPE && typeof detail.message === "string") {
				lines.push(`  - Nurse ${nurseId}${nurseName}: ${t(detail.message)}`);
			} else {
				const reason = detail.error_type ? `${detail.error_type}: ${detail.error}` : detail.error;
				lines.push(`  - Nurse ${nurseId}${nurseName}: ${reason}`);
			}
		}
		lines.push("");
	}

	const conflicts = shiftsResult?.conflicts ?? [];
	if (conflicts.length > 0) {
		lines.push(t("onboarding_load.conflicts.summary_header"));
		for (const c of conflicts) {
			const who = c.nurse_name || c.employee_name || `ID ${c.nurse_id}`;
			const optionText = c.options.map((o) => conflictOptionLabel(o, t)).join(t("onboarding_load.conflicts.option_separator"));
			lines.push(`  - ${who}: ${optionText}`);
		}
		lines.push("");
	}

	// PTO rows the nurse already had — skipped so one existing PTO can't fail the whole load
	const skippedPtos = shiftsResult?.skipped_ptos ?? [];

	if (skippedPtos.length > 0) {
		lines.push(t("onboarding_load.skipped_pto.summary_header"));
		for (const pto of skippedPtos) {
			const name = pto.nurse_name || pto.employee_name || `ID ${pto.nurse_id}`;
			lines.push(`  - ${name}: ${formatDate(pto.start_date)} – ${formatDate(pto.end_date)} (${pto.reason}) — ${describePtoConflict(pto.blocked_by, t)}`);
		}
		lines.push("");
	}

	if (lines.length === 0) {
		lines.push("No issues found.");
	}

	return lines.join("\n").trim();
}

// --- Interactive Conflict Resolution ---

function ConflictResolutionSection({ conflicts, departmentId }: { conflicts: ShiftPtoConflict[]; departmentId?: string }) {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const resolveConflicts = useResolveShiftConflicts();
	const [choices, setChoices] = useState<Record<string, number>>({});
	const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());

	const pending = conflicts.filter((c) => !resolvedIds.has(c.conflict_id));
	const allChosen = pending.length > 0 && pending.every((c) => choices[c.conflict_id] !== undefined);

	const handleApply = useCallback(async () => {
		if (!departmentId) {
			showSnackbar(t("onboarding_load.conflicts.select_department"), "error");
			return;
		}
		const decisions: ConflictDecision[] = pending.map((c) => ({ conflict_id: c.conflict_id, nurse_id: c.nurse_id, keep: c.options[choices[c.conflict_id]] }));
		track("resolve_shift_conflicts_start", { department_id: departmentId, conflict_count: decisions.length });
		const result = await resolveConflicts.mutateAsync({ departmentId, decisions }).catch(() => null);
		if (!result) {
			trackError("resolve_shift_conflicts_error", { department_id: departmentId, conflict_count: decisions.length });
			return;
		}
		track("resolve_shift_conflicts_success", { department_id: departmentId, applied: result.applied, skipped: result.skipped, failed: result.errors.length });
		const failedIds = new Set(result.errors.map((e) => e.conflict_id));
		setResolvedIds((prev) => {
			const next = new Set(prev);
			for (const c of pending) if (!failedIds.has(c.conflict_id)) next.add(c.conflict_id);
			return next;
		});
		const message = t("onboarding_load.conflicts.apply_result", {
			applied: result.applied,
			skipped: result.skipped,
			errorNote: result.errors.length > 0 ? t("onboarding_load.conflicts.failed_suffix", { n: result.errors.length }) : "",
			reactivatedNote: result.reactivated_nurses.length > 0 ? t("onboarding_load.conflicts.reactivated_suffix", { n: result.reactivated_nurses.length }) : "",
		});
		showSnackbar(message, result.errors.length > 0 ? "warning" : "success");
	}, [departmentId, pending, choices, resolveConflicts, showSnackbar, track, trackError, t]);

	const heading = (
		<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: pending.length > 0 ? "warning.main" : "text.secondary" }}>
			{t("onboarding_load.conflicts.title", { n: pending.length })}
		</Typography>
	);

	if (pending.length === 0) return <Box>{heading}</Box>;

	return (
		<Box>
			{heading}
			<Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
				{t("onboarding_load.conflicts.instructions")}
			</Typography>
			{pending.map((conflict) => {
				const nurseName = conflict.nurse_name || conflict.employee_name || "";
				const selected = choices[conflict.conflict_id];
				return (
					<Box
						key={conflict.conflict_id}
						data-testid={`conflict-row-${conflict.conflict_id}`}
						sx={{ mb: 1.5, p: 1.5, borderWidth: 1, borderStyle: "solid", borderColor: "warning.main", borderRadius: 1 }}
					>
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
							{nurseName ? `${nurseName} (ID: ${conflict.nurse_id})` : `ID: ${conflict.nurse_id}`}
						</Typography>
						<RadioGroup
							value={selected === undefined ? "" : String(selected)}
							onChange={(e) => setChoices((prev) => ({ ...prev, [conflict.conflict_id]: Number(e.target.value) }))}
						>
							{conflict.options.map((option, idx) => (
								<FormControlLabel
									key={idx}
									value={String(idx)}
									control={<Radio size="small" data-testid={`conflict-option-${conflict.conflict_id}-${idx}`} />}
									label={conflictOptionLabel(option, t)}
								/>
							))}
						</RadioGroup>
					</Box>
				);
			})}
			<SystemButton data-testid="conflict-resolution-apply" disabled={!allChosen} loading={resolveConflicts.isPending} onClick={handleApply}>
				{resolveConflicts.isPending ? t("onboarding_load.conflicts.applying") : t("onboarding_load.conflicts.apply")}
			</SystemButton>
		</Box>
	);
}

// --- Composite Result Display ---

type LoadResultDisplayProps = {
	result: LoadShiftsResult | LoadRecurringResult | null;
	departmentId?: string;
	timezone?: string;
};

export function LoadResultDisplay({ result, departmentId, timezone }: LoadResultDisplayProps) {
	const { t } = useTranslation();
	const summaryText = useMemo(() => (result ? buildShiftsSummaryText(result, t) : ""), [result, t]);

	if (!result) return null;

	const successCount = result.succed_nurses.length;
	const reactivatedCount = result.reactivated_nurses.length;
	const parsingErrorCount = result.parsing_error.length;
	const notFoundCount = result.not_found_nurses.length;
	const ambiguousCount = result.ambigious_nurses.length;
	const shiftsResult = isShiftsResult(result) ? result : null;
	const failedShiftsCount = result.failed_shifts.length;
	const overlapCount = shiftsResult?.overlap_shifts.length ?? 0;
	const noTimeCount = shiftsResult?.no_time_nurses.length ?? 0;
	const skippedPtoCount = shiftsResult?.skipped_ptos?.length ?? 0;

	return (
		<Box sx={{ mt: 2, mb: 2 }}>
			<CopyableSummaryBox text={summaryText} title={t("onboarding_load.summary.title")} />
			{result.validation_summary && (
				<CollapsibleSummaryBox text={result.validation_summary} title={t("onboarding_load.validation_summary.title")} timezone={timezone} />
			)}

			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				{/* Successful Matches */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: successCount > 0 ? "#2e7d32" : "text.secondary" }}>
						Successful Matches ({successCount})
					</Typography>
					{result.succed_nurses.map((match, idx) => (
						<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
							{match.employee_name} → {match.nurse_name}
						</Typography>
					))}
				</Box>

				{/* Reactivated Employees — were inactive in this department but have shifts in the loaded file */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: reactivatedCount > 0 ? "#2e7d32" : "text.secondary" }}>
						Reactivated Employees ({reactivatedCount})
					</Typography>
					{result.reactivated_nurses.map((match, idx) => (
						<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
							{match.employee_name} → {match.nurse_name}
						</Typography>
					))}
				</Box>

				{/* Parsing Errors */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: parsingErrorCount > 0 ? "#d32f2f" : "text.secondary" }}>
						Parsing Errors ({parsingErrorCount})
					</Typography>
					{result.parsing_error.map((err) => (
						<Typography key={`${err.row}-${err.name}`} variant="body2" sx={{ mb: 0.5 }}>
							Row {err.row} – {err.name}: {err.reason}
						</Typography>
					))}
				</Box>

				{/* Failed Shifts */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: failedShiftsCount > 0 ? "#d32f2f" : "text.secondary" }}>
						Failed Shifts ({failedShiftsCount})
					</Typography>
					{result.failed_shifts.map((f, idx) => {
						if (!Array.isArray(f) || f.length !== 2) {
							return (
								<Typography key={idx} variant="body2" sx={{ mb: 1, color: "#d32f2f" }}>
									Unexpected format: {JSON.stringify(f)}
								</Typography>
							);
						}
						const [nurse_id, error_detail] = f;
						return <FailedShiftCard key={`${idx}-${nurse_id}`} nurse_id={nurse_id} error_detail={error_detail} />;
					})}
				</Box>

				{/* Overlapping Shifts (shifts only) */}
				{shiftsResult && (
					<Box>
						<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: overlapCount > 0 ? "#d32f2f" : "text.secondary" }}>
							Overlapping Shifts ({overlapCount})
						</Typography>
						{shiftsResult.overlap_shifts.map((overlap, idx) => {
							const formatTime = (isoStr: string) => {
								const dt = new Date(isoStr);
								const dateStr = dt.toLocaleDateString();
								const timeStr = dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
								return `${dateStr} ${timeStr}`;
							};
							const nurseName = overlap.nurse_name || overlap.employee_name || "";
							return (
								<Box key={idx} sx={{ mb: 1, p: 1.5, border: "1px solid #ef5350", borderRadius: 1 }}>
									<Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
										{nurseName}
										{nurseName ? ` (ID: ${overlap.nurse_id})` : `ID: ${overlap.nurse_id}`}
									</Typography>
									<Typography variant="body2" sx={{ mb: 0.25 }}>
										Shift 1: {formatTime(overlap.shift1.start_time)} - {formatTime(overlap.shift1.end_time)}
									</Typography>
									<Typography variant="body2">
										Shift 2: {formatTime(overlap.shift2.start_time)} - {formatTime(overlap.shift2.end_time)}
									</Typography>
								</Box>
							);
						})}
					</Box>
				)}

				{shiftsResult && <ConflictResolutionSection conflicts={shiftsResult.conflicts ?? []} departmentId={departmentId} />}

				{/* Shifts Without Time (shifts only) */}
				{shiftsResult && (
					<Box>
						<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: noTimeCount > 0 ? "#d32f2f" : "text.secondary" }}>
							Shifts Without Time ({noTimeCount})
						</Typography>
						{shiftsResult.no_time_nurses.map((item, idx) => {
							const formatted = formatItem(item);
							return formatted ? (
								<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
									{formatted}
								</Typography>
							) : null;
						})}
					</Box>
				)}

				{/* PTO Not Loaded (shifts only) — skipped, not an error; each row says what blocked it */}
				{shiftsResult && (
					<Box>
						<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: skippedPtoCount > 0 ? "#e65100" : "text.secondary" }}>
							{t("onboarding_load.skipped_pto.title", { n: skippedPtoCount })}
						</Typography>
						{(shiftsResult.skipped_ptos ?? []).map((pto, idx) => {
							const nurseName = pto.nurse_name || pto.employee_name || "";
							return (
								<Typography key={`${pto.nurse_id}-${idx}`} variant="body2" sx={{ mb: 0.5 }}>
									{nurseName ? `${nurseName} (ID: ${pto.nurse_id})` : `ID: ${pto.nurse_id}`} – {formatDate(pto.start_date)} – {formatDate(pto.end_date)} (
									{pto.reason}) — {describePtoConflict(pto.blocked_by, t)}
								</Typography>
							);
						})}
					</Box>
				)}

				{/* Not Found Nurses */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: notFoundCount > 0 ? "#d32f2f" : "text.secondary" }}>
						Not Found Nurses ({notFoundCount})
					</Typography>
					{result.not_found_nurses.map((item, idx) => {
						const formatted = formatItem(item);
						return formatted ? (
							<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
								{formatted}
							</Typography>
						) : null;
					})}
				</Box>

				{/* Ambiguous Nurses */}
				<Box>
					<Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: ambiguousCount > 0 ? "#d32f2f" : "text.secondary" }}>
						Ambiguous Nurses ({ambiguousCount})
					</Typography>
					{result.ambigious_nurses.map((item, idx) => (
						<Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
							{item.name}
							{item.suspects.length > 0 && ` → suspects: ${item.suspects.join(", ")}`}
						</Typography>
					))}
				</Box>
			</Box>
		</Box>
	);
}
